import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { sanitizeText } from "@/lib/sanitize";
import { MapPin, User, Phone, Mail, Building, Home, Building2, ExternalLink, FileCheck, Briefcase } from "lucide-react";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { db, auth } from "@/lib/firebase";
import { collection, query, where, getDocs, doc, getDoc } from "firebase/firestore";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { dualWriteJob } from "@/lib/firestore-helpers";

type Job = {
  id: string;
  title: string;
  description: string;
  location: string;
  status: "not_started" | "in_progress" | "completed" | "pending_review";
  client_name?: string;
  client_email?: string;
  client_phone?: string;
  complex_name: string;
  unit_number: string;
  created_at: string;
  assigned_user_id: string;
  property_id: string | null;
  // Compliance fields
  permitType?: string | null;
  jobScope?: string | null;
  projectType?: 'residential' | 'commercial' | null;
};

type JobInfoProps = {
  job: Job;
  onUpdate: () => void;
  contractorId: string;
};

export const JobInfo = ({ job, onUpdate, contractorId }: JobInfoProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [canUpdateStatus, setCanUpdateStatus] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [propertyInfo, setPropertyInfo] = useState<{ name: string; address: string } | null>(null);

  useEffect(() => {
    checkPermissions();
    fetchPropertyInfo();
  }, [job.id]);

  const checkPermissions = async () => {
    const user = auth.currentUser;
    if (!user) return;

    // Check if user has permission to update status
    const rolesQuery = query(
      collection(db, "user_roles"),
      where("userId", "==", user.uid)
    );

    const rolesSnapshot = await getDocs(rolesQuery);
    const roleList = rolesSnapshot.docs.map(doc => doc.data().role);

    const hasAdminOrManager = roleList.includes("admin") || roleList.includes("manager");
    const isAssigned = user.uid === job.assigned_user_id;

    setCanUpdateStatus(hasAdminOrManager || isAssigned);
  };

  const fetchPropertyInfo = async () => {
    if (!job.property_id) return;

    try {
      const propertyDoc = await getDoc(doc(db, "properties", job.property_id));
      
      if (propertyDoc.exists()) {
        const data = propertyDoc.data();
        setPropertyInfo({
          name: data.name,
          address: data.address
        });
      }
    } catch (error) {
      console.error("Error fetching property:", error);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    setUpdating(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Not authenticated");

      await dualWriteJob(contractorId, job.id, {
        status: newStatus as Job['status']
      });

      toast({
        title: "Status Updated",
        description: `Job status changed to ${newStatus.replace(/_/g, " ")}`,
      });

      onUpdate();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Property Information */}
      {propertyInfo && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Property
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="space-y-1">
                <p className="font-semibold">{sanitizeText(propertyInfo.name)}</p>
                <p className="text-sm text-muted-foreground">{sanitizeText(propertyInfo.address)}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(`/properties/${job.property_id}`)}
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                View
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Permit Details</CardTitle>
            <div className="flex items-center gap-2">
              {canUpdateStatus ? (
                <Select 
                  value={job.status} 
                  onValueChange={handleStatusChange}
                  disabled={updating}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="not_started">Not Started</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="pending_review">Pending Review</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <StatusBadge status={job.status} />
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {job.description && (
            <div>
              <h3 className="font-semibold mb-2">Description</h3>
              <p className="text-muted-foreground">{sanitizeText(job.description)}</p>
            </div>
          )}

          {/* Permit Type and Job Scope */}
          {(job.permitType || job.jobScope) && (
            <div className="grid grid-cols-2 gap-4 p-3 rounded-lg bg-muted/50">
              {job.permitType && (
                <div className="flex items-start gap-2">
                  <FileCheck className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm">Trade/Permit Type</h3>
                    <p className="text-muted-foreground capitalize">{job.permitType.replace(/_/g, " ")}</p>
                  </div>
                </div>
              )}
              {job.jobScope && (
                <div className="flex items-start gap-2">
                  <Briefcase className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-sm">Permit Scope</h3>
                    <p className="text-muted-foreground capitalize">{job.jobScope.replace(/_/g, " ")}</p>
                  </div>
                </div>
              )}
            </div>
          )}

          {job.location && (
            <div className="flex items-start gap-2">
              <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <h3 className="font-semibold">Location</h3>
                <p className="text-muted-foreground">{sanitizeText(job.location)}</p>
              </div>
            </div>
          )}

          {job.complex_name && (
            <div className="flex items-start gap-2">
              <Building className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <h3 className="font-semibold">Complex</h3>
                <p className="text-muted-foreground">{sanitizeText(job.complex_name)}</p>
              </div>
            </div>
          )}

          {job.unit_number && (
            <div className="flex items-start gap-2">
              <Home className="h-5 w-5 text-muted-foreground mt-0.5" />
              <div>
                <h3 className="font-semibold">Unit Number</h3>
                <p className="text-muted-foreground">{sanitizeText(job.unit_number)}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {job.client_name && (
        <Card>
          <CardHeader>
            <CardTitle>Client Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-muted-foreground" />
              <span>{sanitizeText(job.client_name)}</span>
            </div>
            {job.client_email && (
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-muted-foreground" />
                <a href={`mailto:${job.client_email}`} className="text-primary hover:underline">
                  {sanitizeText(job.client_email)}
                </a>
              </div>
            )}
            {job.client_phone && (
              <div className="flex items-center gap-2">
                <Phone className="h-5 w-5 text-muted-foreground" />
                <a href={`tel:${job.client_phone}`} className="text-primary hover:underline">
                  {sanitizeText(job.client_phone)}
                </a>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Project Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Created:</span>
            <span className="font-semibold">
              {job.created_at ? format(new Date(job.created_at), "MMM d, yyyy") : "N/A"}
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
